import { CommonModule } from '@angular/common';
import { Component, WritableSignal } from '@angular/core';
import { AbstractControl, FormArray, FormGroup } from '@angular/forms';
import {
  FieldWrapper,
  FormlyFieldConfig,
  FormlyFieldProps,
} from '@ngx-formly/core';
import { TranslatePipe } from '@ngx-translate/core';
import { Button } from 'primeng/button';
import { Drawer } from 'primeng/drawer';
import { InputText } from 'primeng/inputtext';
import { CustomDrawerComponent } from '../../_shared/_components/drawer/drawer.component';
import { FORM_IMPORTS } from '../../_shared/_imports/form.import';

export interface DrawerWrapperProps extends FormlyFieldProps {
  position?: typeof Drawer.prototype.position;
  buttonLabel?: string;
  setter?: any;
  status?: boolean | undefined;
  formatLabel: any;
  isTextView: boolean | undefined;
  onShow?: WritableSignal<any>;
  labelColor?: string;
  warning?: string;
  showFooter?: boolean;
  canClose?: () => boolean;
}

@Component({
  selector: 'drawer-wrapper-primeng-form-field',
  standalone: true,
  imports: [
    CommonModule,
    ...FORM_IMPORTS,
    InputText,
    Button,
    TranslatePipe,
    CustomDrawerComponent,
  ],
  template: `
    @if (props.isTextView) {
      <div class="w-full flex flex-col gap-2 p-field">
        <div class="flex items-baseline flex-wrap">
          <label class="ml-(--p-inputtext-padding-x)" [innerHTML]="props.label">
          </label>
          @if (displayError) {
            <small
              class="text-(--p-form-field-invalid-placeholder-color) italic ml-(--p-inputtext-padding-x)"
            >
              @if (formControl.errors) {
                <formly-validation-message
                  class="ui-message-text"
                  [field]="field"
                ></formly-validation-message>
              } @else {
                {{ 'formly.validation.required' | translate }}
              }
            </small>
          }
        </div>
        <div class="w-full" [innerHTML]="props.placeholder"></div>
        <p-button
          [label]="props.buttonLabel ?? 'Set'"
          size="small"
          class="self-end"
          [disabled]="props.readonly ?? false"
          (onClick)="props.readonly ? null : (visible = true)"
        ></p-button>
      </div>
    } @else {
      <div class="p-field">
        <div class="flex items-baseline flex-wrap">
          <label class="ml-(--p-inputtext-padding-x)" [innerHTML]="props.label">
          </label>
          @if (displayError) {
            <small
              class="text-(--p-form-field-invalid-placeholder-color) italic ml-(--p-inputtext-padding-x)"
            >
              @if (formControl.errors) {
                <formly-validation-message
                  class="ui-message-text"
                  [field]="field"
                ></formly-validation-message>
              } @else {
                {{ 'formly.validation.required' | translate }}
              }
            </small>
          }
        </div>

        <input
          pInputText
          readonly
          [placeholder]="props.placeholder"
          [type]="'text'"
          class="w-full {{
            displayError ? 'ng-dirty ng-invalid p-invalid' : ''
          }} {{ props.readonly ? 'bg-(--p-select-disabled-background)' : '' }}"
          [value]="value() ?? ''"
          [disabled]="props.readonly ?? false"
          (click)="props.readonly ? null : (visible = true)"
        />
      </div>
    }
    @if (props.description) {
      <small
        class="italic ml-(--p-inputtext-padding-x)"
        [innerHTML]="props.description"
      ></small>
    }

    @if (props.warning) {
      <small
        class="italic text-(--p-message-warn-color) ml-(--p-inputtext-padding-x)"
        [innerHTML]="props.warning"
      ></small>
    }

    <custom-drawer
      [(visible)]="visible"
      (onShow)="onShow()"
      (onHide)="onHide()"
    >
      <ng-template #header>
        <div class="title" [innerHTML]="props.label"></div>
      </ng-template>
      <ng-template #content>
        <div
          class="flex flex-col gap-4 px-(--p-dialog-header-padding) py-28 h-fit min-h-full"
        >
          <ng-container #fieldComponent></ng-container>
        </div>
      </ng-template>

      @if (props?.showFooter ?? true) {
        <ng-template #footer>
          <div class="w-full text-right">
            <p-button
              [label]="props.buttonLabel ?? 'Set'"
              size="small"
              (onClick)="set()"
            ></p-button>
          </div>
        </ng-template>
      }
    </custom-drawer>
  `,
})
export class DrawerWrapperFormField extends FieldWrapper<
  FormlyFieldConfig<DrawerWrapperProps>
> {
  visible: boolean = false;

  get displayError(): boolean {
    const submitted = !!this.field.options?.parentForm?.submitted;
    const interaction = this.hasInteraction(this.formControl);

    const customError =
      this.formControl.invalid &&
      (interaction || submitted || this.props.status === false);

    return this.showError || customError;
  }

  onHide() {
    this.props.onShow?.set(undefined);
  }

  onShow() {
    this.props.onShow?.set('onshow');
  }

  value() {
    if (this.formControl.valid) {
      return this.props.formatLabel(this.field);
    } else {
      return null;
    }
  }

  set() {
    const canClose = this.props.canClose
      ? this.props.canClose()
      : this.formControl.valid;

    if (canClose) {
      if (this.props.isTextView) {
        this.visible = false;
        this.onHide();
      } else {
        this.props.setter(this.field).then((res: string) => {
          if (res) {
            this.visible = false;
            this.onHide();
          }
        });
        this.props.status = this.formControl.valid;
      }
      if (!this.formControl.valid) {
        this.markForValidation(this.formControl);
      }
    } else {
      this.markForValidation(this.formControl);
      this.props.status = false;
    }
  }

  private markForValidation(control: AbstractControl) {
    control.markAsTouched();
    control.markAsDirty();

    if (control instanceof FormGroup || control instanceof FormArray) {
      Object.values(control.controls).forEach((child) => {
        this.markForValidation(child);
      });
    }

    control.updateValueAndValidity({ onlySelf: true });
  }

  private hasInteraction(control: AbstractControl): boolean {
    if (control.touched || control.dirty) {
      return true;
    }

    if (control instanceof FormGroup || control instanceof FormArray) {
      return Object.values(control.controls).some((child) =>
        this.hasInteraction(child),
      );
    }

    return false;
  }
}
